import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Crown, ArrowUpRight, Flame, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { LEVEL_DESCRIPTIONS } from "@/lib/siteContent";

const ZONE_MEDIA = {
  casual: { img: "https://images.unsplash.com/photo-1541126274323-dbac58d14741?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "New Jack Free Play", blurb: LEVEL_DESCRIPTIONS.casual },
  golden_era: { img: "https://images.unsplash.com/photo-1593078166039-c9878df5c520?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "Rare Grooves", blurb: LEVEL_DESCRIPTIONS.golden_era },
  new_and_now: { img: "https://images.unsplash.com/photo-1640604542825-8d934dc2e5d1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", tag: "New Jack Free Play", blurb: "Start Here." },
};
const ACCENT = { casual: "#FAFAFA", golden_era: "#F59E0B", new_and_now: "#06B6D4" };

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);

  useEffect(() => {
    api.get("/game/zones").then(({ data }) => setZones(data.zones)).catch(() => {});
  }, [user]);

  const openZone = (z) => {
    if (z.locked) { navigate("/premium"); return; }
    if (!user) { navigate("/login"); return; }
    navigate(`/play/${z.id}`);
  };

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-white/10">
        <img src="https://images.unsplash.com/photo-1611063158871-7dd3ed4a2ac8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.12]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-mono-caps text-xs text-primary mb-5">Daily Hip Hop Crosswords</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="font-display text-5xl sm:text-7xl lg:text-8xl max-w-4xl">
            HIP HOP KNOWLEDGE<br /><span className="text-primary">DROP</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="mt-6 max-w-xl text-base sm:text-lg text-muted-foreground">
            Test your hip hop IQ across two levels. Solve the daily grid, build streaks, climb the ranks, and prove you know the culture.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mt-9 flex flex-wrap gap-3">
          
              
             
            
          </motion.div>
          <div className="mt-12 flex flex-wrap gap-8 font-mono-caps text-[10px] text-muted-foreground">
            <span className="flex items-center gap-2"><Flame size={14} className="text-primary" /> Per-lEVEL Streaks</span>
            <span className="flex items-center gap-2"><Trophy size={14} className="text-secondary" /> Daily Leaderboards</span>
            <span className="flex items-center gap-2"><Crown size={14} className="text-primary" /> Premium Unlocks</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display text-3xl sm:text-4xl mb-2">CHOOSE YOUR LEVEL</h2>
        <p className="text-muted-foreground text-sm mb-10 font-mono-caps text-[10px]">Two difficulty levels · Both cover Hip Hop from 1972 to the present.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
         {["casual", "golden_era"].map((zoneId) => {
  const z = zones.find((item) => item.id === zoneId);
  if (!z) return null;

  const media = ZONE_MEDIA[z.id];

  return (
    <motion.div
      key={z.id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group relative border border-white/15 bg-card overflow-hidden hover-lift cursor-pointer "
      onClick={() => openZone(z)}
      data-testid={`zone-card-${z.id}`}
    >
      <div className="relative h-64">
        <img
          src={media.img}
          alt={z.name}
          className="w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />

        {z.locked && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/80 border border-primary/50 px-3 py-1.5">
            <Lock size={13} className="text-primary" />
            <span className="font-mono-caps text-[9px] text-primary">Premium</span>
          </div>
        )}

        {!z.locked && z.access === "free" && (
          <div className="absolute top-4 right-4 bg-secondary text-secondary-foreground font-mono-caps text-[9px] px-3 py-1.5">
            Free
          </div>
        )}
      </div>

      <div className="p-6 sm:p-8 -mt-16 relative">
        <p className="font-mono-caps text-[10px] mb-2" style={{ color: ACCENT[z.id] }}>
          {media.tag}
        </p>

        <h3 className="font-display text-3xl sm:text-4xl mb-2">
          {z.id === "casual" ? "NEW JACK" : "TRIPLE OG"}
        </h3>

        <p className="text-sm text-muted-foreground mb-5 max-w-md">
          {media.blurb}
        </p>

        <span className="inline-flex items-center gap-1.5 font-mono-caps text-[10px] group-hover:text-primary transition-colors">
          {z.locked ? "Unlock with Premium" : "Play now"} <ArrowUpRight size={14} />
        </span>
      </div>
    </motion.div>
  );
})}
        </div>
      </section>
    </Layout>
  );
}
